// @ts-check
// Diálogo do nome do fluxo (spec 2.0.0, fluxos.md seção 20.2; contrato 09 de fluxos, seção 17): ao criar (em branco
// ou por modelo) e ao clicar no nome dentro do editor. Nome obrigatório até 120 letras; nome repetido só avisa; a
// coleção é opcional, com as coleções que já existem como sugestão.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { MAXIMUM_FLOW_COLLECTION_LENGTH, MAXIMUM_FLOW_NAME_LENGTH } from '../../../../shared/flows/flow-limits.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { FlowDialog } from './flow-dialog.js';
import { FlowField } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ mode: 'create' | 'edit', initialName?: string, initialCollection?: string | null, collections?: string[], existingNames?: string[],
 *   currentFlowId?: string | null, onSubmit: (value: { name: string, collection: string | null }) => Promise<boolean | void> | boolean | void,
 *   onClose: () => void, submitLabel?: string }} props
 */
export function FlowNameDialog({ mode, initialName = '', initialCollection = null, collections = [], existingNames = [], onSubmit, onClose, submitLabel }) {
  const [name, setName] = useState(initialName);
  const [collection, setCollection] = useState(initialCollection ?? '');
  const [touched, setTouched] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const trimmed = name.trim();
  const nameError = touched && !trimmed ? t('flows.nameDialog.required') : null;
  const isRepeated = trimmed !== '' && trimmed !== initialName.trim()
    && existingNames.some((existing) => normalizeSearchText(existing) === normalizeSearchText(trimmed));

  const submit = async () => {
    setTouched(true);
    if (!trimmed || isBusy) return;
    setIsBusy(true);
    try {
      const result = await onSubmit({ name: trimmed.slice(0, MAXIMUM_FLOW_NAME_LENGTH), collection: collection.trim() ? collection.trim().slice(0, MAXIMUM_FLOW_COLLECTION_LENGTH) : null });
      if (result === false) setIsBusy(false);
    } catch {
      setIsBusy(false);
    }
  };

  return html`<${FlowDialog} title=${mode === 'create' ? t('flows.nameDialog.createTitle') : t('flows.nameDialog.editTitle')} onClose=${onClose}
    onSubmit=${submit} testId="flow-name-dialog"
    footer=${html`<${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${onClose} />
      <${Button} title=${submitLabel ?? t('flows.nameDialog.continue')} accent="blue" size="small" type="submit" isLoading=${isBusy} />`}>
    <p class="flow-dialog-text">${t('flows.nameDialog.text')}</p>
    <${FlowField} label=${t('flows.nameDialog.name')} htmlFor="flow-name-input" error=${nameError}
      hint=${isRepeated ? t('flows.nameDialog.repeated') : null}>
      <input id="flow-name-input" class="text-input" value=${name} maxLength=${MAXIMUM_FLOW_NAME_LENGTH} required data-autofocus="true"
        aria-invalid=${nameError ? 'true' : undefined} onInput=${(/** @type {InputEvent} */ event) => setName(/** @type {HTMLInputElement} */ (event.currentTarget).value)}
        onBlur=${() => setTouched(true)} />
    <//>
    <${FlowField} label=${t('flows.nameDialog.collection')} htmlFor="flow-collection-input" hint=${t('flows.nameDialog.collectionHint')}>
      <input id="flow-collection-input" class="text-input" value=${collection} maxLength=${MAXIMUM_FLOW_COLLECTION_LENGTH} list="flow-collection-options"
        onInput=${(/** @type {InputEvent} */ event) => setCollection(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
      <datalist id="flow-collection-options">${collections.map((value) => html`<option key=${value} value=${value} />`)}</datalist>
    <//>
  <//>`;
}
